import { ChecklistRun, PauseDuration } from '@/firebase/checklistRunService';

/**
 * Calculate total active time for a checklist run
 * Active time = time spent working (excluding pause periods)
 */
export function calculateActiveTime(run: ChecklistRun, currentTime?: Date): number {
  if (!run.startedAt) return 0;

  const startedAt = run.startedAt?.toDate 
    ? run.startedAt.toDate() 
    : (run.startedAt instanceof Date ? run.startedAt : new Date());
  
  const now = currentTime || new Date();
  
  // Start with the cumulative active time (already saved active time from previous sessions)
  let activeTimeSeconds = run.activeTimeSeconds || 0;

  // If currently in progress, add time since last resume
  if (run.status === 'in_progress' && run.lastResumedAt) {
    const lastResumedAt = run.lastResumedAt?.toDate 
      ? run.lastResumedAt.toDate() 
      : (run.lastResumedAt instanceof Date ? run.lastResumedAt : null);
    
    // Only add time if lastResumedAt is valid and in the past
    if (lastResumedAt && lastResumedAt.getTime() <= now.getTime()) {
      const activeTimeSinceResume = Math.floor((now.getTime() - lastResumedAt.getTime()) / 1000);
      // Only add if it's a positive value (sanity check)
      if (activeTimeSinceResume > 0) {
        activeTimeSeconds += activeTimeSinceResume;
      }
    }
  }

  return activeTimeSeconds;
}

/**
 * Calculate total time from start to now/completion
 * Total time = active time + pause time
 */
export function calculateTotalTime(run: ChecklistRun, currentTime?: Date): number {
  if (!run.startedAt) return 0;

  const startedAt = run.startedAt?.toDate 
    ? run.startedAt.toDate() 
    : (run.startedAt instanceof Date ? run.startedAt : new Date());
  
  const endTime = run.completedAt 
    ? (run.completedAt.toDate ? run.completedAt.toDate() : 
       (run.completedAt instanceof Date ? run.completedAt : new Date()))
    : (currentTime || new Date());

  return Math.floor((endTime.getTime() - startedAt.getTime()) / 1000);
}

/**
 * Calculate pause time from pause durations array
 */
export function calculatePauseTime(run: ChecklistRun, currentTime?: Date): number {
  const pauseDurations = run.pauseDurations || [];
  const now = currentTime || new Date();
  
  let totalPauseSeconds = 0;

  for (const pause of pauseDurations) {
    if (pause.pausedAt) {
      const pausedAt = pause.pausedAt?.toDate 
        ? pause.pausedAt.toDate() 
        : (pause.pausedAt instanceof Date ? pause.pausedAt : new Date());
      
      let resumedAt: Date;
      
      if (pause.resumedAt) {
        resumedAt = pause.resumedAt.toDate 
          ? pause.resumedAt.toDate() 
          : (pause.resumedAt instanceof Date ? pause.resumedAt : new Date());
      } else if (run.status === 'paused' && pause === pauseDurations[pauseDurations.length - 1]) {
        // Last pause hasn't resumed yet - calculate to now
        resumedAt = now;
      } else {
        // Pause entry exists but no resume time (shouldn't happen, but handle gracefully)
        continue;
      }

      const pauseDuration = Math.floor((resumedAt.getTime() - pausedAt.getTime()) / 1000);
      totalPauseSeconds += pauseDuration;
    }
  }

  return totalPauseSeconds;
}

/**
 * Format seconds to human-readable time string
 * Examples: "1h 23m", "45m", "2m 30s", "30s"
 */
export function formatTimeDisplay(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (secs > 0 && hours === 0) {
    // Only show seconds if less than an hour
    parts.push(`${secs}s`);
  }

  return parts.join(' ') || '0s';
}

/**
 * Get time display strings for a checklist run
 */
export function getTimeDisplayStrings(run: ChecklistRun): {
  activeTime: string;
  totalTime: string;
  pauseTime: string;
  activeTimeSeconds: number;
  totalTimeSeconds: number;
  pauseTimeSeconds: number;
} {
  const activeTimeSeconds = calculateActiveTime(run);
  const totalTimeSeconds = calculateTotalTime(run);
  const pauseTimeSeconds = calculatePauseTime(run);

  return {
    activeTime: formatTimeDisplay(activeTimeSeconds),
    totalTime: formatTimeDisplay(totalTimeSeconds),
    pauseTime: formatTimeDisplay(pauseTimeSeconds),
    activeTimeSeconds,
    totalTimeSeconds,
    pauseTimeSeconds,
  };
}

